import React, { FC, useEffect, useState } from "react";
import { Button, Collapse, Input, InputRef, Select, Pagination, Space, Table, Tooltip, Spin } from "antd";
import Highlighter from "react-highlight-words";
import { cloneDeep, find } from "lodash";

import PinUnpin from "@pages/member-details/pin-unpin-component";
import CustomTooltip from "@pages/tool-tip";
import './labs.scss'
import { useLabsStore, LabsStore } from "@stores/labsStore";
import { LabsProvider, useLabs, useLabsContext } from './useLabs'
import { SearchOutlined, CaretDownOutlined } from "@ant-design/icons";
import { centricClientCheck, formatDisplayDate } from "@utils/helpers";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { TableLocale } from "@components/noDataFound";

const { Panel } = Collapse;

const rowsPerPageArray: any[] = [
  "5",
  "10",
  "15",
  "20",
  "25",
  "30",
  "35",
  "40",
  "45",
  "50",
];

export const Search: FC<
  {
    defaultValue: any,
    inputRef: React.RefObject<InputRef>
    apply: (value: any) => void
    reset: () => void
  }
> = (props) => {
  const [value, setvalue] = React.useState<string>(props.defaultValue || '')
      
  return (
    <div style={{ padding: "10px" }}>
      <Input
        ref={props.inputRef}
        style={{ marginBottom: 8, display: "block" }}
        value={value}
        onChange={(e) => {
          e.preventDefault();
          setvalue(e.target.value)
        }}
        onKeyPress={(e: any) => {
          if (e.key === "Enter") {
            e.preventDefault();
            props.apply && props.apply(value)
          }
        }}
        onPressEnter={(e: any): any => {
          e.stopPropagation();
        }}
      />
      <Space>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
          onClick={(e) => {
            props.apply && props.apply(value)
          }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setvalue('')
            props.reset && props.reset()
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div> 
  )
}

const getFilterConfig = (
  name: string,
  store: LabsStore | undefined,
  inputRef: React.RefObject<InputRef>,
  refresh: any,
  setPage: any
) => {
  const {
    filters,
    setFilter,
    clearFilter
  } = store || {};
  
  const filter = (filters || []).find(i => i.columnName === name)
  return {
    filterIcon: (filtered: any) => <SearchOutlined style={{ color: filter ? "#EF6D2A" : undefined }} />,
    filterDropdown: ({ confirm }: any) => <Search
      defaultValue={
        filter?.searchText || ''
      }
      inputRef={inputRef}
      apply={
        (value) => {
          setFilter && setFilter(name, value);
          setPage && setPage(1);
          if(refresh) { 
            let filtersAdded = cloneDeep(filters);
            const isFilterSearched = find(filters, {"columnName": name });
            if(isFilterSearched) {
              filtersAdded = [...(filtersAdded ? filtersAdded?.filter((item:any) => item.columnName !== name) : []), ...( value && value !== "" ? [{ "columnName": name, "searchText": value }] : []) ]
            } else {
              filtersAdded = [...(filtersAdded ? filtersAdded : []), { "columnName": name, "searchText": value } ]
            }
            refresh(["1"], { filters: filtersAdded, page: 1 })
          }
          confirm({ closeDropdown: true })
        }
      }
      reset={
        () => {
          let filtersAdded = cloneDeep(filters);
          const isFilterSearched = find(filters, {"columnName": name });
          if(isFilterSearched) {
            filtersAdded = filtersAdded?.filter((item:any) => item.columnName !== name);
            refresh(["1"], { filters: filtersAdded })
          }
          clearFilter && clearFilter(name);
          setPage && setPage(1);
          confirm({ closeDropdown: true, page: 1 })
        }
      }
    />,
    onFilterDropdownOpenChange: (visible:any) => {
      if (visible) {
        setTimeout(() => inputRef.current?.select(), 100);
      }
    },
  }
}

const Labs: FC<any> = (props) => {
  const isCentric = centricClientCheck();
  const {
    labs,
    count,
    loading,
    store,
    refresh
  } = useLabsContext();

  const inputRef = React.useRef<InputRef>(null);

  const {
    params,
    setPage,
    setLimit,
    filters,
  } = store || ({} as LabsStore)

  const {
    page,
    limit,
    sortBy,
    sortType
  } = params

  const highlighterColorCoded = (text: any, textTosearch: string, style: React.CSSProperties = {}) => {
    return (
      <span style={{ display: "flex", justifyContent: isCentric ? "left" : "center", ...style }}>
        <Tooltip placement="topLeft" title={text ? text.toString() : ""}>
          <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[textTosearch]} autoEscape textToHighlight={text ? `${text.toString()}` : ""} />
        </Tooltip>
      </span>
    );
  };

  return (
    <div className='ui-labs'>
      <div className="ui-labs__table">
        <Spin spinning={loading} indicator={<HealthemSpinner size={70}/>}>
        <Table
          bordered={true} 
          pagination={false}
          onChange={(p, f, sort: any) => {
            if (Array.isArray(sort)) {
              sort = sort.pop()
            }
            store && store.setSorting(sort?.column?.dataIndex, sort?.order?.replace('end', '').toUpperCase());
            if(refresh) { 
              refresh(["1"], { sortBy: sort?.column?.dataIndex, sortType: sort?.order?.replace('end', '').toUpperCase() })
            }
          }}
          columns={[
            {
              
              dataIndex: "lab_date",
              key: "date",
              sorter: { },
              sortOrder: sortBy === 'lab_date' ? sortType==="ASC" ? "ascend" : "descend" : null,
              render: (date: any) => {
                const formatedDate = formatDisplayDate(date, "createDateFormat");
                const filter = (filters || []).find(i => i.columnName === 'lab_date')
                return highlighterColorCoded(formatedDate, filter?.searchText || '');
              },
              width: 100,
              title: () => {
                return "DATE";
              },
              ...getFilterConfig('lab_date', store, inputRef, refresh, setPage)
            },
            {
              dataIndex: "lab_description",
              key: "desc",
              sorter: {},
              sortOrder: sortBy === 'lab_description' ? sortType==="ASC" ? "ascend" : "descend" : null,
              render: (desc: any) => {
                const filter = (filters || []).find(i => i.columnName === 'lab_description')
                return highlighterColorCoded(desc, filter?.searchText || '', { justifyContent: 'flex-start' });
              },
              width: 250,
              title: () => {
                return "DESCRIPTION";
              },
              ...getFilterConfig('lab_description', store, inputRef, refresh, setPage)
            },
            {
              
              dataIndex: "lab_value",
              key: "value",
              sorter: !isCentric && {},
              sortOrder: !isCentric && sortBy === 'lab_value' ? sortType==="ASC" ? "ascend" : "descend" : null,
              render: (value: any, record: any) => {
                const filter = (filters || []).find(i => i.columnName === 'lab_value')
                return <span className={`ui-labs__table__value--${record.in_range}`}>
                  {highlighterColorCoded(value, filter?.searchText || '')}
                </span>;
              },
              width: 100,
              title: () => {
                return "VALUE";
              },
              ...getFilterConfig('lab_value', store, inputRef, refresh, setPage)
            },
            {
              
              dataIndex: "lab_units",
              key: "units",
              sorter: false,
              render: (units: any) => {
                return highlighterColorCoded(units, "");
              },
              filterIcon: (filtered: any) => <SearchOutlined />,
              width: 100,
              title: () => {
                return "UNITS";
              },
            },
            {
              
              dataIndex: "lab_range",
              key: "range",
              sorter: false,
              render: (range: any) => {
                return highlighterColorCoded(range, "");
              },
              filterIcon: (filtered: any) => <SearchOutlined />,
              width: 100,
              title: () => {
                return "REF RANGE";
              },
            },
          ]}
          dataSource={labs?.values}
          locale={TableLocale}
        />
        {count > 0 && (
          <div className="pagination">
            <div>
              Show
              <Select
                value={limit}
                style={{ width: 64, margin: "8px 8px" }}
                onChange={(e) => {
                  setLimit && setLimit(e);
                  setPage && setPage(1);
                  if(refresh) { 
                    refresh(["1"], { limit: e, page: 1 })
                  }
                }}
                suffixIcon={<CaretDownOutlined />}
              >
                {rowsPerPageArray.map((val) => (
                  <Select.Option key={val} value={val}>{val}</Select.Option>
                ))}
              </Select>
              rows per page
            </div>
            <Pagination
              total={count}
              showTotal={(total, range) => (
                <>
                  <span style={{ color: "#091e42" }}>
                    {
                      [
                        (limit * (page - 1)) + 1,
                        '-',
                        Math.min(count, (limit * page))
                      ].join(' ')
                    }
                  </span>
                  <span> of {count}</span>
                </>
              )}
              pageSize={limit}
              defaultPageSize={limit}
              defaultCurrent={page}
              current={page}
              showSizeChanger={false}
              onChange={(e) => {
                setPage && setPage(e);
                if(refresh) {
                  refresh(["1"], { page: e })
                }
              }}
            />
          </div>
        )}
        </Spin>
      </div>
    </div>
  )
}

const View: FC<any> = (props) => {
  const dataCardId = "38";
  const [activeCardPanel, setActiveCardPanel] = useState<any>(props?.activeKey?.includes(dataCardId) ? ["1"] : [])
  const isCentric = centricClientCheck();
  const {
    noData,
    store, 
    labs,
    refresh, 
    loading,
    patientID,
    patientId
  } = useLabsContext()
  const {
    fetchRedValues,
    clearStore,
    setfetchRedValues,
    setPage,
    setLimit,
    resetParams,
    resetFilter
  } = store || ({} as LabsStore)

  useEffect(() => {
    setActiveCardPanel(props?.activeKey?.includes(dataCardId) ? ["1"] : [])
    if(props?.activeKey?.includes(dataCardId) && patientID !== patientId) {
      refresh && refresh(['1']);
    }
  }, [props?.activeKey])

  const onCollapseChange = () => {
    if(activeCardPanel.length > 0){ 
      props?.setActivePanels(cloneDeep(props?.activeKey)?.filter((item:any) => item !== dataCardId))
    }
    else {
      props?.setActivePanels([...(props?.activeKey ? props?.activeKey : []), dataCardId]);
    };
  }

  const genExtra = React.useCallback(
    (id: any) => {
      return (
        <div className="ui-labs__collapse__panel__extra" >
          <Button
            disabled={noData}
            className={`ui-labs__collapse__panel__extra__button ui-labs__collapse__panel__extra__button--${fetchRedValues ? 'active' : 'inactive'}`}
            onClick={
              e => {
                e.preventDefault()
                e.stopPropagation()
                setfetchRedValues(!fetchRedValues);
                setPage && setPage(1);
                refresh && refresh(["1"], { fetchRedValues: !fetchRedValues, page: 1 });
              }
            }
          >
            <span className="ui-labs__collapse__panel__extra__button__point"></span>
            View Red Values
          </Button>
          <PinUnpin marginRight={"16px"} dataCardId={id}
      changeHandler={props?.pinChangeHandler}
      pinCardIds={props.pinCardIds} />
        </div>
      )
    },
    [fetchRedValues, setfetchRedValues, props?.pinChangeHandler, props.pinCardIds, noData]
  );

  const getHeading = (headerValue: any) => (
    <>
      <span>{headerValue}</span>
      <CustomTooltip
        toolTipvalue={headerValue}
        children={
          <span
            style={{ margin: "7px 8px" }}
            className="icon icon-info clr-lightGrayColor"
          ></span>
        }
      />
    </>
  );

  useEffect(() => {
    if (patientId !== patientID) {
      clearStore();
      setPage(1);
      setLimit(5);
      resetParams();
      resetFilter();
      setfetchRedValues(false);
    }
  }, [patientID]);


  return (
    <>
      <Collapse className="ui-labs__collapse" accordion activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
        <Panel header={getHeading(isCentric ? "Labs Data" : "Labs")} key="1" className="title ui-labs__collapse__panel" extra={genExtra(dataCardId)} style={{ marginBottom: "8px" }}>
          <Labs/>
        </Panel>
      </Collapse>
    </>
  );
};

const Wrapper: React.FC<any> = function (props) {
  return (
    <LabsProvider >
      <View {...props} />
    </LabsProvider>
  )
};

export default Wrapper
